import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import { Select } from '@sm360/ui-library'
import { useTranslation } from '@sm360/hooks'

import { ADOBE_TAGS, adobeService, getInventoryTypeFromHubCategories } from '@sm360/helpers'
import { ListingContext } from '../../configs/listing-view/state.manager'
import { pageSortingDummies } from '../../dummies/pageSorting.dummies'

const PageSorting = ({ extraClasses }) => {
    const {
        state: {
            vehicleCriteria,
            urlParams,
            pagination,
            websiteVehiclesPerPage,
            hubName,
            hubCategories,
            dealerInfos: { lang },
        },
        addVehicleCriteria,
        dispatch,
    } = React.useContext(ListingContext)

    const { t } = useTranslation()

    const baseClass = 'il-pageSorting'
    const classNames = clsx(baseClass, extraClasses)

    const totalNoOfItems = pagination?.numberOfItems || 0

    /**
     * Handle Vehicle per Page Sorting Selected
     * @param { String } value - Selected value
     * @returns {Promise<void>}
     */
    const handleChange = async (value) => {
        const criteria = addVehicleCriteria(vehicleCriteria, { limit: value })

        const inventoryType = getInventoryTypeFromHubCategories(hubCategories, hubName, lang)

        adobeService.eventTrigger(ADOBE_TAGS.SEARCH_RESULT_PAGE_LOAD_MORE(inventoryType), ADOBE_TAGS.EVENT_TYPES.GLOBAL_ONCLICK)

        // Update Vehicle Criteria State
        dispatch({ type: 'updateVehicleCriteria', vehicleCriteria: criteria })
    }

    // Dynamically build options list, including websiteVehiclesPerPage if it's not in the default list
    const getOptions = () => {
        const defaultOptions = pageSortingDummies.limit.values
        const defaultValues = defaultOptions.map((opt) => opt.value)

        // If websiteVehiclesPerPage exists and is not in the default options, add it
        if (websiteVehiclesPerPage && !defaultValues.includes(websiteVehiclesPerPage)) {
            const newOption = {
                value: websiteVehiclesPerPage,
                label: websiteVehiclesPerPage,
            }
            // Combine and sort by numeric value
            const allOptions = [...defaultOptions, newOption].sort((a, b) => a.value - b.value)
            return allOptions
        }

        return defaultOptions
    }

    const options = getOptions()

    // Use vehicleCriteria.limit first (current selection), then urlParams.limit, then default
    const selectedValue = vehicleCriteria?.limit || urlParams?.limit || pageSortingDummies.limit.defaultValue
    // Ensure the value is a string to match the option values
    const displayValue = selectedValue ? selectedValue : pageSortingDummies.limit.defaultValue

    return (
        <div className={classNames}>
            <label htmlFor='pageSorting' className={`${baseClass}__label`}>
                {t('filters.pageSorting.labelBefore')}
            </label>

            <Select
                id='pageSorting'
                name='pageSorting'
                disabled={totalNoOfItems < pageSortingDummies.limit.defaultValue}
                className={`${baseClass}__select`}
                options={options}
                value={displayValue}
                onChange={(value) => handleChange(value)}
            />

            <label htmlFor='pageSorting' className={`${baseClass}__label`}>
                {t('filters.pageSorting.labelAfter')}
            </label>
        </div>
    )
}

PageSorting.propTypes = {
    /** Extra Classes */
    extraClasses: PropTypes.string,
}

PageSorting.defaultProps = {
    extraClasses: '',
}

export default PageSorting
